import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { SERVICES } from "../data/services";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const inputCls =
  "rounded-none bg-obsidian border-olive-deep text-foreground placeholder:text-coyote/60 focus-visible:ring-1 focus-visible:ring-khaki focus-visible:border-khaki";

export const RequestForm = ({ selectedService, onServiceChange }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", description: "" });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (selectedService) onServiceChange(selectedService);
  }, [selectedService, onServiceChange]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedService) {
      toast.error("SELECT A SERVICE OPERATION", { description: "Choose the unit you need deployed." });
      return;
    }
    if (!date) {
      toast.error("SET A PREFERRED DATE", { description: "Pick a target date for the service call." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/requests", {
        ...form,
        service_type: selectedService,
        preferred_date: format(date, "yyyy-MM-dd"),
      });
      setConfirmation(res.data);
      toast.success("TRANSMISSION RECEIVED", { description: `Request ${res.data.id.slice(0, 8).toUpperCase()} logged with command.` });
    } catch (err) {
      toast.error("TRANSMISSION FAILED", { description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="deploy" className="border-t border-olive-deep py-24 sm:py-32 blueprint-grid" data-testid="deploy-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="overline-tactical text-coyote mb-4" data-testid="deploy-overline">OP-FIX // 04 // MISSION BRIEFING</p>
          <h2 className="font-display uppercase text-4xl sm:text-6xl tracking-tight leading-[0.95]" data-testid="deploy-heading">
            Request A<br /><span className="text-alert">Service Call</span>
          </h2>
          <p className="mt-6 max-w-sm text-sm text-muted-foreground leading-relaxed">
            Complete the briefing. Command reviews every transmission and confirms
            your service window within 24 hours.
          </p>
          <div className="mt-10 border border-olive-deep divide-y divide-olive-deep max-w-sm" data-testid="deploy-assurances">
            {["RESPONSE WITHIN 24 HOURS", "UPFRONT, HONEST PRICING", "LICENSED & INSURED CREWS"].map((t) => (
              <div key={t} className="flex items-center gap-3 px-5 py-3">
                <CheckCircle2 className="w-4 h-4 text-olive" strokeWidth={2} />
                <span className="text-[11px] tracking-[0.2em] text-khaki">{t}</span>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          {confirmation ? (
            <div className="border border-khaki bg-olive-deep/30 p-10 sm:p-14" data-testid="request-success-panel">
              <p className="overline-tactical text-alert mb-4">/// TRANSMISSION CONFIRMED</p>
              <h3 className="font-display uppercase text-3xl sm:text-4xl text-khaki">Briefing Received.</h3>
              <p className="mt-6 text-sm text-muted-foreground leading-relaxed max-w-md">
                Your service request has been logged with command. Stand by — we will
                contact you within 24 hours to confirm your deployment window. A
                confirmation email is on its way to your inbox.
              </p>
              <div className="mt-8 border border-olive-deep p-5 inline-block">
                <span className="text-[10px] tracking-[0.3em] text-coyote block">REQUEST ID</span>
                <span className="font-mono text-lg text-foreground" data-testid="request-confirmation-id">{confirmation.id.toUpperCase()}</span>
              </div>
              <div className="mt-10">
                <button
                  onClick={() => { setConfirmation(null); setForm({ name: "", email: "", phone: "", address: "", description: "" }); setDate(null); }}
                  data-testid="request-new-button"
                  className="text-xs tracking-[0.25em] text-khaki border border-olive-deep px-6 py-3 hover:bg-olive-deep transition-colors duration-300"
                >
                  &gt;&gt; FILE ANOTHER REQUEST
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-olive-deep bg-obsidian/80 p-6 sm:p-10 space-y-6" data-testid="request-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-coyote mb-2">FULL NAME *</label>
                  <Input required value={form.name} onChange={set("name")} placeholder="J. DOE" className={inputCls} data-testid="request-name-input" />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-coyote mb-2">PHONE *</label>
                  <Input required type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 010-0100" className={inputCls} data-testid="request-phone-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-coyote mb-2">EMAIL *</label>
                  <Input required type="email" value={form.email} onChange={set("email")} placeholder="YOU@EXAMPLE.COM" className={inputCls} data-testid="request-email-input" />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-coyote mb-2">SERVICE ADDRESS *</label>
                  <Input required value={form.address} onChange={set("address")} placeholder="123 MAIN ST" className={inputCls} data-testid="request-address-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-coyote mb-2">SERVICE OPERATION *</label>
                  <Select value={selectedService || ""} onValueChange={onServiceChange}>
                    <SelectTrigger className="rounded-none bg-obsidian border-olive-deep text-foreground focus:ring-khaki" data-testid="request-service-select">
                      <SelectValue placeholder="SELECT OPERATION" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-obsidian border-olive-deep max-h-72">
                      {SERVICES.map((s) => (
                        <SelectItem key={s.code} value={s.name} className="text-xs" data-testid={`service-option-${s.code.toLowerCase()}`}>
                          {s.code} — {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-coyote mb-2">PREFERRED DATE *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="request-date-button"
                        className="w-full flex items-center justify-between border border-olive-deep bg-obsidian px-3 py-2 text-sm text-foreground hover:border-khaki transition-colors duration-300"
                      >
                        {date ? format(date, "PPP") : <span className="text-coyote/60">PICK TARGET DATE</span>}
                        <CalendarIcon className="w-4 h-4 text-khaki" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-none bg-obsidian border-olive-deep" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                        data-testid="request-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.3em] text-coyote mb-2">SITUATION REPORT *</label>
                <Textarea
                  required
                  rows={4}
                  value={form.description}
                  onChange={set("description")}
                  placeholder="DESCRIBE THE JOB: WHAT, WHERE, AND ANYTHING THE CREW SHOULD KNOW."
                  className={`${inputCls} resize-none`}
                  data-testid="request-description-input"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="request-submit-button"
                className="dogtag w-full bg-alert text-white font-bold text-sm tracking-[0.25em] py-4 hover:bg-khaki hover:text-obsidian transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "TRANSMITTING..." : "[ TRANSMIT REQUEST ]"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
